import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { RotateCcw, X } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useCommands, useCommandActions, useDevices } from '../hooks/useBiometricAdmin';
import { istDateTime, type CommandStatus } from '../biometricTypes';

const STATUS_TONE: Record<CommandStatus, BadgeTone> = {
  pending: 'warning',
  sent: 'info',
  acked: 'success',
  failed: 'danger',
  expired: 'neutral',
};

/** Live view of the outbound command queue, with retry and cancel. */
export function CommandQueuePanel() {
  const { data: devices } = useDevices(0);
  const [sn, setSn] = useState('');
  const [status, setStatus] = useState('');
  const { data: commands, isLoading } = useCommands(sn || undefined, status || undefined);
  const { retry, cancel } = useCommandActions();

  const act = async (fn: Promise<unknown>, message: string) => {
    try {
      await fn;
      toast.success(message);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <Card className="space-y-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">Command queue</h2>
          <p className="text-sm text-on-surface-variant">
            Commands wait here until the device next polls. A device that never polls leaves them pending —
            check that it is online.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            className="h-9 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
            value={sn}
            onChange={e => setSn(e.target.value)}
          >
            <option value="">All devices</option>
            {(devices ?? []).map(d => (
              <option key={d.serial_number} value={d.serial_number}>
                {d.name || d.serial_number}
              </option>
            ))}
          </select>
          <select
            className="h-9 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="">Any status</option>
            {(['pending', 'sent', 'acked', 'failed', 'expired'] as CommandStatus[]).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : !commands?.length ? (
        <p className="py-8 text-center text-sm text-on-surface-variant">The queue is empty.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-outline-variant text-left text-xs uppercase tracking-wide text-on-surface-variant">
                <th className="px-2 py-2">#</th>
                <th className="px-2 py-2">Device</th>
                <th className="px-2 py-2">Command</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Result</th>
                <th className="px-2 py-2">Queued</th>
                <th className="px-2 py-2 text-right">—</th>
              </tr>
            </thead>
            <tbody>
              {commands.map(cmd => (
                <tr key={cmd._id} className="border-b border-outline-variant/50 hover:bg-surface-variant/30">
                  <td className="px-2 py-1.5 font-mono text-xs">{cmd.cmd_id}</td>
                  <td className="px-2 py-1.5 font-mono text-xs text-on-surface-variant">{cmd.device_sn}</td>
                  <td className="max-w-[280px] truncate px-2 py-1.5 font-mono text-xs" title={cmd.command}>
                    {cmd.command}
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge tone={STATUS_TONE[cmd.status]}>{cmd.status}</Badge>
                    {(cmd.attempts ?? 0) > 1 && (
                      <span className="ml-1 text-xs text-on-surface-variant">×{cmd.attempts}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    {cmd.return_code === null || cmd.return_code === undefined ? (
                      <span className="text-on-surface-variant">—</span>
                    ) : (
                      <span className={cmd.return_code >= 0 ? 'text-success' : 'text-danger'}>
                        {cmd.return_message || cmd.return_code}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-xs text-on-surface-variant">
                    {istDateTime(cmd.created_at)}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <div className="flex justify-end gap-1">
                      {(cmd.status === 'failed' || cmd.status === 'expired') && (
                        <button
                          type="button"
                          title="Queue this command again"
                          className="rounded p-1 text-on-surface-variant hover:bg-surface-variant"
                          onClick={() => act(retry.mutateAsync(cmd._id), 'Command re-queued.')}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {cmd.status === 'pending' && (
                        <button
                          type="button"
                          title="Cancel this pending command"
                          className="rounded p-1 text-danger hover:bg-danger/10"
                          onClick={() => act(cancel.mutateAsync(cmd._id), 'Command cancelled.')}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
